import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001/api';

const InvoicePage = () => {
  const { invoiceId } = useParams();
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [signatureApplied, setSignatureApplied] = useState(false);
  const [signatureImage, setSignatureImage] = useState(null);
  const [signedDate, setSignedDate] = useState(null);
  
  // Fetch invoice data
  useEffect(() => {
    const fetchInvoice = async () => {
      if (!invoiceId) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/invoices/${invoiceId}`);
        const data = response.data;
        
        setInvoiceData({
          id: data.id,
          invNumber: data.inv_number,
          invDate: data.inv_date,
          dueDate: data.due_date,
          customer: {
            name: data.customer_name,
            email: data.customer_email,
            phone: data.customer_phone,
            company: data.customer_company || ""
          },
          sellerVat: "VAT-ZA-123456789",
          plan: data.plan,
          term: data.term,
          qty: data.qty,
          currency: data.currency,
          unitPrice: data.unit_price,
          discount: data.discount,
          vatRate: 0.15,
          bank: {
            name: "Standard Bank",
            accName: "Ghatti Trading",
            accNo: "000000000",
            branch: "000000"
          },
          notes: "Access & license delivered after payment clears.",
          status: data.status
        });
        
        // If already signed, show signature
        if (data.signature_data) {
          setSignatureImage(data.signature_data);
          setSignatureApplied(true);
          setSignedDate(data.signed_date);
          setTermsAccepted(true);
        }
      } catch (err) {
        console.error('Error fetching invoice:', err);
        alert('Invoice not found or error loading invoice');
      } finally {
        setLoading(false);
      }
    };
    
    fetchInvoice();
  }, [invoiceId]);
  
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [strokes, setStrokes] = useState([]);
  const [currentStroke, setCurrentStroke] = useState([]);

  // Loading state
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0a0a0a',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '1rem' }}>Loading Invoice...</div>
          <div style={{ color: '#888' }}>Please wait</div>
        </div>
      </div>
    );
  }

  // Error state
  if (!invoiceData) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0a0a0a',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '1rem', color: '#ff4444' }}>Invoice Not Found</div>
          <div style={{ color: '#888' }}>The invoice you're looking for doesn't exist or has been removed.</div>
        </div>
      </div>
    );
  }

  // Calculate totals
  const subtotal = invoiceData.unitPrice * invoiceData.qty;
  const discountAmount = subtotal * invoiceData.discount;
  const afterDiscount = subtotal - discountAmount;
  const vatAmount = afterDiscount * invoiceData.vatRate;
  const totalDue = afterDiscount + vatAmount;

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#000';

    // Redraw existing strokes
    strokes.forEach(stroke => {
      ctx.beginPath();
      stroke.forEach((point, index) => {
        if (index === 0) {
          ctx.moveTo(point.x, point.y);
        } else {
          ctx.lineTo(point.x, point.y);
        }
      });
      ctx.stroke();
    });
  }, [strokes]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const point = getCoordinates(e);
    setCurrentStroke([point]);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const point = getCoordinates(e);
    setCurrentStroke(prev => [...prev, point]);
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    ctx.beginPath();
    if (currentStroke.length > 0) {
      const lastPoint = currentStroke[currentStroke.length - 1];
      ctx.moveTo(lastPoint.x, lastPoint.y);
    }
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing && currentStroke.length > 0) {
      setStrokes(prev => [...prev, currentStroke]);
      setCurrentStroke([]);
    }
    setIsDrawing(false);
  };

  const handleUndo = () => {
    if (strokes.length === 0) return;
    setStrokes(prev => prev.slice(0, -1));
    redrawCanvas(strokes.slice(0, -1));
  };

  const handleClear = () => {
    setStrokes([]);
    setCurrentStroke([]);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const redrawCanvas = (strokesToDraw) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    strokesToDraw.forEach(stroke => {
      ctx.beginPath();
      stroke.forEach((point, index) => {
        if (index === 0) {
          ctx.moveTo(point.x, point.y);
        } else {
          ctx.lineTo(point.x, point.y);
        }
      });
      ctx.stroke();
    });
  };

  const handleApplySignature = async () => {
    if (!termsAccepted) {
      alert('Please acknowledge the no-refund policy and agree to the Terms & EULA before signing.');
      return;
    }

    if (strokes.length === 0) {
      alert('Please provide a signature before applying.');
      return;
    }

    const canvas = canvasRef.current;
    const dataURL = canvas.toDataURL('image/png');
    
    // Save signature to database
    try {
      await axios.post(`${API}/invoices/${invoiceId}/sign`, {
        signature_data: dataURL,
        terms_accepted: true
      });
      
      setSignatureImage(dataURL);
      setSignatureApplied(true);
      setSignedDate(new Date().toISOString().split('T')[0]);
      
      alert('Signature applied and saved! Use Print / Save as PDF to keep a signed copy.');
    } catch (err) {
      console.error('Error saving signature:', err);
      alert('Failed to save signature. Please try again.');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0a0a',
      color: '#fff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      padding: '2rem 1rem'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        backgroundColor: '#1a1a1a',
        borderRadius: '12px',
        padding: '2rem',
        border: '1px solid #333'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem', borderBottom: '2px solid #00FF7F', paddingBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{
              width: '50px',
              height: '50px',
              backgroundColor: '#00FF7F',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#000'
            }}>K</div>
            <div>
              <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '300' }}>Kasango AI Pro — Manual Invoice</h1>
              <div style={{ fontSize: '14px', color: '#888', marginTop: '0.25rem' }}>
                Invoice #{invoiceData.invNumber} • {invoiceData.invDate} • Due: {invoiceData.dueDate}
              </div>
            </div>
          </div>
        </div>

        {/* Bill To & From */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
          <div>
            <h3 style={{ fontSize: '14px', textTransform: 'uppercase', color: '#00FF7F', marginBottom: '0.75rem' }}>Bill To</h3>
            <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#ccc' }}>
              <div style={{ fontWeight: '500', color: '#fff' }}>{invoiceData.customer.name || '[Customer Name]'}</div>
              <div>{invoiceData.customer.email || '[Email]'}</div>
              <div>{invoiceData.customer.phone || '[Phone]'}</div>
              <div>{invoiceData.customer.company || '[Company]'}</div>
            </div>
          </div>
          <div>
            <h3 style={{ fontSize: '14px', textTransform: 'uppercase', color: '#00FF7F', marginBottom: '0.75rem' }}>From</h3>
            <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#ccc' }}>
              <div style={{ fontWeight: '500', color: '#fff' }}>Ghatti Trading (Kasango AI)</div>
              <div>VAT: {invoiceData.sellerVat}</div>
              <div>billing@kasango.ai</div>
              <div>+27 75 099 6682</div>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div style={{ 
          backgroundColor: '#222', 
          padding: '1rem', 
          borderRadius: '8px', 
          marginBottom: '2rem',
          border: '1px solid #333'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <div>
              <span style={{ 
                backgroundColor: '#00FF7F', 
                color: '#000', 
                padding: '0.25rem 0.75rem', 
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600'
              }}>{invoiceData.plan}</span>
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#ff4444',
              fontWeight: '600',
              backgroundColor: '#330000',
              padding: '0.25rem 0.75rem',
              borderRadius: '4px'
            }}>All Sales Final • No Refunds</div>
          </div>
          <div style={{ fontSize: '14px', color: '#ccc', marginTop: '0.75rem' }}>
            {invoiceData.term} • {invoiceData.qty} seat • Manual EFT — No automatic charges.
          </div>
        </div>

        {/* Line Items */}
        <table style={{ width: '100%', marginBottom: '2rem', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #333' }}>
              <th style={{ textAlign: 'left', padding: '0.75rem 0', fontSize: '12px', textTransform: 'uppercase', color: '#888' }}>Description</th>
              <th style={{ textAlign: 'right', padding: '0.75rem 0', fontSize: '12px', textTransform: 'uppercase', color: '#888' }}>Unit Price</th>
              <th style={{ textAlign: 'right', padding: '0.75rem 0', fontSize: '12px', textTransform: 'uppercase', color: '#888' }}>Qty</th>
              <th style={{ textAlign: 'right', padding: '0.75rem 0', fontSize: '12px', textTransform: 'uppercase', color: '#888' }}>Line Total</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid #333' }}>
              <td style={{ padding: '1rem 0', fontSize: '14px' }}>{invoiceData.plan} — {invoiceData.term}</td>
              <td style={{ textAlign: 'right', padding: '1rem 0', fontSize: '14px' }}>{invoiceData.currency} {invoiceData.unitPrice.toFixed(2)}</td>
              <td style={{ textAlign: 'right', padding: '1rem 0', fontSize: '14px' }}>{invoiceData.qty}</td>
              <td style={{ textAlign: 'right', padding: '1rem 0', fontSize: '14px' }}>{invoiceData.currency} {subtotal.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>

        {/* Totals */}
        <div style={{ marginLeft: 'auto', maxWidth: '300px', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', fontSize: '14px' }}>
            <span style={{ color: '#888' }}>Subtotal:</span>
            <span>{invoiceData.currency} {subtotal.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', fontSize: '14px' }}>
            <span style={{ color: '#888' }}>Discount:</span>
            <span>{invoiceData.currency} {discountAmount.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', fontSize: '14px', borderBottom: '1px solid #333' }}>
            <span style={{ color: '#888' }}>VAT ({(invoiceData.vatRate * 100).toFixed(0)}%):</span>
            <span>{invoiceData.currency} {vatAmount.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', fontSize: '18px', fontWeight: '600' }}>
            <span>Total Due:</span>
            <span style={{ color: '#00FF7F' }}>{invoiceData.currency} {totalDue.toFixed(2)}</span>
          </div>
        </div>

        {/* Signature Section */}
        {signatureApplied && signatureImage && (
          <div style={{ 
            marginBottom: '2rem', 
            padding: '1rem', 
            backgroundColor: '#222', 
            borderRadius: '8px',
            border: '1px solid #333'
          }}>
            <div style={{ fontSize: '12px', color: '#888', marginBottom: '0.5rem' }}>Client Signature:</div>
            <img src={signatureImage} alt="Signature" style={{ maxWidth: '200px', border: '1px solid #444', borderRadius: '4px', backgroundColor: '#fff' }} />
            <div style={{ fontSize: '12px', color: '#00FF7F', marginTop: '0.5rem' }}>
              Signed on {signedDate}
            </div>
          </div>
        )}

        {/* Payment Instructions */}
        <div style={{ 
          backgroundColor: '#1a2a1a', 
          border: '1px solid #00FF7F33',
          borderRadius: '8px', 
          padding: '1.5rem', 
          marginBottom: '2rem' 
        }}>
          <h3 style={{ fontSize: '16px', marginBottom: '1rem', color: '#00FF7F' }}>Payment Instructions (Manual EFT)</h3>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <div><strong>Bank:</strong> {invoiceData.bank.name}</div>
            <div><strong>Account Name:</strong> {invoiceData.bank.accName}</div>
            <div><strong>Account Number:</strong> {invoiceData.bank.accNo}</div>
            <div><strong>Branch Code:</strong> {invoiceData.bank.branch}</div>
            <div style={{ marginTop: '0.5rem' }}><strong>Reference:</strong> Invoice {invoiceData.invNumber}</div>
          </div>
        </div>

        {/* Terms */}
        <div style={{ 
          backgroundColor: '#2a1a1a', 
          border: '1px solid #ff444433',
          borderRadius: '8px', 
          padding: '1.5rem', 
          marginBottom: '2rem',
          fontSize: '13px',
          lineHeight: '1.6'
        }}>
          <h3 style={{ fontSize: '14px', marginBottom: '0.75rem', color: '#ff6666' }}>Terms & Conditions</h3>
          <p style={{ margin: 0 }}>
            All sales are final. We do not offer refunds once a license key has been issued or platform access has been delivered, except where required by law.
          </p>
          <p style={{ margin: '0.75rem 0 0 0', color: '#888' }}>
            {invoiceData.notes}
          </p>
        </div>

        {/* Terms Checkbox */}
        <div className="no-print" style={{ marginBottom: '2rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
            <input 
              type="checkbox" 
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              style={{ marginRight: '0.75rem', width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span>I acknowledge the no-refund policy and agree to the Terms & EULA.</span>
          </label>
        </div>

        {/* Signature Pad */}
        {!signatureApplied && (
          <div className="no-print" style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '14px', marginBottom: '0.75rem', color: '#00FF7F' }}>Client Signature</h3>
            <canvas
              ref={canvasRef}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              style={{
                width: '100%',
                height: '150px',
                border: '1px solid #333',
                borderRadius: '8px',
                backgroundColor: '#fff',
                cursor: 'crosshair',
                touchAction: 'none'
              }}
            />
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button
                onClick={handleUndo}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#333',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Undo
              </button>
              <button
                onClick={handleClear}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#333',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Clear
              </button>
              <button
                onClick={handleApplySignature}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#00FF7F',
                  color: '#000',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Apply Signature
              </button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="no-print" style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
          <button
            onClick={handlePrint}
            style={{
              flex: 1,
              padding: '0.75rem',
              backgroundColor: '#00FF7F',
              color: '#000',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600'
            }}
          >
            Print / Save as PDF
          </button>
        </div>

        {/* Footer */}
        <div className="no-print" style={{ 
          textAlign: 'center', 
          fontSize: '12px', 
          color: '#666',
          paddingTop: '1rem',
          borderTop: '1px solid #333'
        }}>
          Email POP to billing@kasango.ai. Access & license activated after funds clear.
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background-color: white !important;
          }
          
          .no-print {
            display: none !important;
          }
          
          * {
            color: #000 !important;
            background-color: white !important;
            border-color: #ccc !important;
          }
          
          h1, h2, h3, strong {
            color: #000 !important;
          }
          
          .page-initials::after {
            content: "Client initials: ________";
            display: block;
            margin-top: 2rem;
            font-size: 12px;
            color: #666;
          }
        }
        
        @page {
          margin: 2cm;
        }
      `}</style>
    </div>
  );
};

export default InvoicePage;
